// The room HOST for a per-minute consult, for BOTH roles (contract §9 pins path, default
// export and the `{appointmentId, token, role}` props exactly): "hosts the room and owns
// the heartbeat lifecycle" (gateway-flow-c-design.md §10). Mounted at `/merci/consultation`
// (CheckoutSuccess.jsx, patient, right after the Flow C Checkout Session redirect), at
// `/consultation/salle` (ConsultRoom.jsx, patient, re-entry from the client portal) and at
// `/admin/telehealth/consult/:appointmentId` (admin/telehealth/ConsultRoomPage.jsx,
// practitioner, no token).
//
// BUG #3 fix. `ConsultJoinButton` used to be a plain `<a href="/api/consults/{id}/join">`
// — a 302 that took the browser OFF our app entirely, and nothing on the practitioner
// side ever heartbeated at all. With no page of ours left running, the only presence
// evidence was the join instant: `raw == 0`, `no_show_unknown`, hold cancelled — a
// 60-minute consult paid nothing. This component now EMBEDS the room in an `<iframe>`
// (`GET /api/consults/{id}/access`, contract §2) once `canJoin`, and runs the heartbeat
// for as long as it stays mounted — patient or practitioner — so presence is real evidence
// instead of a single click.
//
// BUG #7 fix. The patient's poll used to be `GET /api/appointments/{id}/session`, which
// is OWNER-ONLY — a 401 every 4 s for the whole visit — while the join gate was read from
// a ONE-SHOT quote fetch that never refreshed, so the door never unlocked without a
// manual reload. `useConsultStatus` (`GET /api/consults/{id}/status`) is the patient-safe
// read that actually re-polls; the practitioner variant omits the token and is identified
// by the owner session, same convention as `getConsultAccess`.
//
// THE ROOM NOW HAS AN END (docs/booking-module/telehealth-room-lifecycle.md). A booking
// carries three server-published instants — `endingSoonAt`, `scheduledEndAt` and
// `closesAt` (the HARD CLOSE) — and a `presence` sub-document written by the `room_watch`
// job. This frame renders the sentence each one licences, and takes the room down at the
// hard close. Every one of those decisions is resolved in `gateway/roomPhase.js`, which
// the practitioner's page reads too, so the two screens cannot describe one call
// differently.
import React, { useCallback, useEffect, useRef, useState } from "react";
import { useT, useLanguage } from "@/lib/LanguageContext";
// Relative: gateway/ has no "@/" Jest shim — resolved for real, matching every other
// gateway-consuming component in this build.
import defaultGatewayApi from "../gateway/api";
import { useConsultQuote } from "../gateway/hooks/useConsultQuote";
import { useConsultStatus } from "../gateway/hooks/useConsultStatus";
import { useConsultHeartbeat } from "../gateway/hooks/useConsultHeartbeat";
import { extractGatewayErrorCode } from "../gateway/errors";
import { isFixedBlock, isPaidFixedBlock, isPerMinute } from "../gateway/contract";
import { roomPhase, isConsultOver } from "../gateway/roomPhase";
import { fmtMoney } from "../admin/money";
import { Loader2, AlertTriangle, Clock, Maximize2, Minimize2 } from "lucide-react";
import ConsultReauthPanel from "./ConsultReauthPanel";

// F-15 fix. This component used to mount the room on `canJoin` alone, and `canJoin`
// answered only the MONEY question — so `/merci/consultation`, which mounts this frame
// the instant the hold lands, opened and JOINED a consultation 26 hours in the future.
// The session flipped to `in_progress` and the patient's own manage-link cancel was then
// refused by the (correct) CONSULT_IN_PROGRESS gate: they had paid and could not give
// the slot back. `joinState` (contract §2) is the server's answer to the OTHER question,
// and the iframe is now gated on it; before the window this page states the authorisation
// and the instant the room opens, and the `/status` poll flips it by itself.
//
// F-16 fix, layer one. `pagehide` fires a `navigator.sendBeacon` departure so the span
// ends when the tab actually closed rather than at the last heartbeat up to 20 s earlier.
// It is best effort by construction — the billing half of F-16 lives in the pure clock
// (`sessions.presence_horizons`), which refuses to bill a party who stopped proving they
// were there whether or not this beacon ever arrives.

// `joinState` -> the sentence shown INSTEAD of the room while it is not `open`. A lookup
// table rather than a template key, so a fourth state the backend adds later falls back
// to the generic waiting copy instead of rendering a raw identifier (the convention in
// `ManageBooking.jsx`'s REFUSAL_KEYS and `calendarUtils.js`'s ERROR_CODE_KEYS).
// An INSTANT rendered in the reader's own locale — the same three-locale map
// `ClientPortal.jsx` uses, spelled here rather than imported because that one is a local
// helper of a page, not an exported utility, and a second import edge between two public
// pages for one date format would be the wrong dependency to create.
const FMT_LOCALES = { fr: "fr-CA", es: "es-ES", en: "en-US" };

export const fmtWhen = (value, lang) => {
  if (!value) return "";
  try {
    return new Date(value).toLocaleString(FMT_LOCALES[lang] || FMT_LOCALES.en,
                                          { dateStyle: "medium", timeStyle: "short" });
  } catch {
    return String(value);
  }
};

// A TIME OF DAY, in the reader's own locale — the same three-locale map, deliberately
// NOT `admin/booking/calendarUtils.localeFor`: the comment above refuses that import
// edge, and one date format is not worth a dependency from `public/` into `admin/`.
//
// Separate from `fmtWhen` because it answers a different question. `fmtWhen` dates an
// instant that may be days away ("your room opens on 14 Sep, 09:00"); this one names a
// moment inside the visit the reader is already in, where the date is noise and the
// clock time is the whole content ("your booked time ends at 09:30").
export const fmtClock = (value, lang) => {
  if (!value) return "";
  try {
    return new Date(value).toLocaleTimeString(FMT_LOCALES[lang] || FMT_LOCALES.en,
                                              { hour: "2-digit", minute: "2-digit" });
  } catch {
    return String(value);
  }
};

const JOIN_STATE_KEYS = {
  not_yet_open: "consult.room.joinState.notYetOpen",
  closed: "consult.room.joinState.closed",
};

// F-20 fix. "Waiting for the payment authorisation." was the whole of what a patient was
// told, for ever, when `payment_intent.amount_capturable_updated` never arrived — no
// elapsed time, no statement that anything was being done, nothing to distinguish a
// two-second wait from a wedged one. The server now publishes `awaitingSince` (its own
// instant: the browser's mount time resets on every reload and would under-report exactly
// the long waits that matter) and `authReconcileAfterSeconds`, the instant past which it
// starts confirming the authorisation with Stripe itself.
//
// THE DURATION IS ASSEMBLED FROM DICTIONARY KEYS, not from a number and a unit written
// here: "s" and "min" are user-facing text and the language law admits no third category
// for tokens that happen to be short.
//
// `fmtDuration`, not `fmtElapsed`, and its keys moved from `consult.room.waitingAuth.*`
// to `consult.room.duration.*` with it: the ending-soon and overrun countdowns are a
// second caller, and they are counting DOWN to a booked instant, so a key path that says
// "the authorisation wait" would have been a lie about two of the three call sites.
export const fmtDuration = (t, seconds) => (
  seconds < 60
    ? t("consult.room.duration.seconds", { count: seconds })
    : t("consult.room.duration.minutes", { count: Math.floor(seconds / 60) })
);

const SESSION_STATUS_KEYS = {
  scheduled: "consult.room.status.scheduled",
  in_progress: "consult.room.status.in_progress",
  computing: "consult.room.status.computing",
  computed: "consult.room.status.computed",
  captured: "consult.room.status.captured",
  void_no_show: "consult.room.status.void_no_show",
  cancelled: "consult.room.status.cancelled",
  // No `expired` member: the backend dropped it from `SESSION_STATES` (an eighth
  // value nothing ever set — a lapsed hold is `cancelled`). The PAYMENT state
  // `expired` is a different vocabulary and has nothing to do with this map.
};

/**
 * @param {string} appointmentId
 * @param {?string} [token] the patient's `manage_token` capability; absent for the
 *   practitioner, who is identified by the owner session instead.
 * @param {"patient"|"practitioner"} [role] defaults from whether `token` was supplied.
 * @param {function} [onStatus] called with the latest `GET /consults/{id}/status`
 *   payload every time it changes (a fresh poll result, never an unrelated re-render) —
 *   `admin/telehealth/ConsultRoomPage.jsx` reads billed minutes / cap-reached off it to
 *   render the practitioner's in-room completion controls without this component having
 *   to know anything about them.
 * @param {object} [gatewayApi] DI seam (contract §9)
 * @param {import("react").ReactNode} [endedSlot] WHAT ELSE BELONGS ON THE EXIT SCREEN, decided
 *   by the mount site. Rendered inside the `consult-room-ended` block, so it appears exactly
 *   once the server says the consultation is over and never before. The frame stays ignorant
 *   of what it is: the patient's mount passes the review invitation, the practitioner's mount
 *   passes nothing, and a third surface could pass something else — none of which is a reason
 *   for this component to learn about reviews.
 * @param {?string} [homePath] BUG-055 — WHERE THIS PATIENT GETS BACK TO. A path, passed by
 *   the mount site rather than resolved here, because the way out of a room is a property
 *   of where the room was mounted and not of the room: the patient's is the practice site
 *   (`useTenantHomePath`, the BUG-039 three-way rule, which needs the tenant document),
 *   the practitioner's is `/admin/telehealth` and their page already carries it. Absent →
 *   no link is rendered, which is exactly right for the practitioner mount.
 */
export default function ConsultRoomFrame({
  appointmentId,
  token,
  role = token ? "patient" : "practitioner",
  onStatus,
  gatewayApi = defaultGatewayApi,
  homePath = "",
  endedSlot = null,
}) {
  const t = useT("public");
  // `lang` is read (not merely subscribed to) because the join window's `opensAt` is an
  // INSTANT and a time is not a translatable string: it is formatted in the reader's own
  // locale, the way `ClientPortal`/`ManageBooking` already format every appointment time.
  const { lang } = useLanguage();

  const isPatient = role === "patient";
  const statusToken = isPatient ? token : undefined;

  // The gate BOTH roles poll — never `getConsultSession` from here (BUG #7).
  const { data: status, loading: statusLoading, error: statusError } =
    useConsultStatus(appointmentId, statusToken, { poll: true }, gatewayApi);
  // WHICH MODEL SOLD THIS CONSULTATION, stated by the server (contract §2). A FIXED BLOCK
  // was paid in full at booking: there is no ceiling to quote and no clock to feed.
  //
  // THE TWO GATES BELOW DEFAULT IN OPPOSITE DIRECTIONS, and that is deliberate, because
  // being wrong costs opposite amounts. `status` is null until the first poll lands, and a
  // server that never sends the field at all is the same absence.
  //   * NOT HEARTBEATING a per-minute consultation is a delivered visit that bills ZERO —
  //     the defect `useConsultHeartbeat`'s own docstring is about. So the clock runs unless
  //     we positively know this is a fixed block, and a few useless beats on one are the
  //     cheap side of that trade.
  //   * FETCHING the quote for a fixed block is a guaranteed `PRICING_NOT_SET`, and the
  //     quote is money DISPLAY only, so that one waits until we positively know.
  // A FIXED BLOCK WHOSE CHARGE NEVER LANDED IS NOT "AUTHORISED, BUT NOT YET", AND IS NOT
  // "WAITING FOR AUTHORISATION" EITHER. It has no `awaitingSince`, so the money branch
  // below (`waitedSeconds`) — the one that keeps an unauthorised per-minute consultation
  // away from the join-window line, for exactly this reason — can never fire for it: an
  // abandoned checkout was told "Payment authorised — your room opens on ..." before the
  // window and "we are confirming your authorisation" inside it. Money before time here
  // too, at every point in the window, exactly as the server's own gate order has it.
  const blockNotPaid = isFixedBlock(status) && status != null && !isPaidFixedBlock(status);
  // Money display only, and patient-only: `GET /appointments/{id}/quote?token=` has no
  // owner-session variant, so a practitioner mount never calls it (appointmentId is
  // withheld, which the hook treats as "nothing to load"). It waits until the model is
  // POSITIVELY per-minute: a late ceiling reminder costs nothing, and one fetched during
  // the unknown window would leave a "you will be charged up to X" on a consultation that
  // has no ceiling — the hook keeps its last value when its id is withdrawn.
  const { data: quote } = useConsultQuote(
    isPatient && isPerMinute(status) ? appointmentId : null, token, gatewayApi);
  // Withheld by APPOINTMENT ID, the same way `useConsultQuote` is withheld above: the
  // hook's own first guard is `if (!appointmentId) return` — "nothing to do" — whereas a
  // null token or role would leave it running and merely change which endpoint it called.
  useConsultHeartbeat(isFixedBlock(status) ? null : appointmentId, token,
                      { intervalMs: 20000, role }, gatewayApi);

  // `useConsultStatus` sets a FRESH object on every poll tick, so this only re-fires on
  // an actual new status (or the initial load) — never on an unrelated re-render (the
  // access-door effect below, a language switch, …) leaving `status` untouched.
  useEffect(() => {
    if (onStatus) onStatus(status);
  }, [status, onStatus]);

  // `canJoin` is the SERVER's conjunction of the two gates (money and time); `joinState`
  // says which of them is missing, and is what this page renders when the answer is "not
  // yet". Never re-derived here: a browser deciding for itself that the room is open is
  // exactly the class of bug F-15 was.
  const canJoin = Boolean(status?.canJoin);
  const joinState = status?.joinState || null;
  const roomNotOpen = joinState != null && joinState !== "open";
  // BUG-055 — IS THIS CONSULTATION OVER? `gateway/roomPhase.isConsultOver` (lifted out of
  // this file so the practitioner's page resolves it identically) reads the server's own
  // two facts, one per pricing model, and never a local clock.
  //
  // WHY THE DOOR'S OWN ANSWER IS NOT ENOUGH, and why this exists. `canJoin` is the money
  // gate AND the time gate, and `PAID_BOOKING_STATUSES` (consults.py) admits `completed`
  // DELIBERATELY, so that a delivered consultation is turned away by the TIME gate with
  // `CONSULT_WINDOW_CLOSED` rather than told its payment failed. The consequence, measured
  // 2026-09-09: for the rest of the join window a completed consultation kept `canJoin:
  // true`, so the provider frame stayed mounted — the patient sat in a live third-party
  // video room on a page whose own status line read "This consultation is over."
  const consultOver = isConsultOver(status);
  const [access, setAccess] = useState(null);
  const [accessError, setAccessError] = useState(null);

  // ── TWO CLOCKS, ONE DISCIPLINE ────────────────────────────────────────────────
  //
  // THE CLOCK IS THE SERVER'S, NOT THE BROWSER'S. `status.now` is the server's instant at
  // the moment of the read, so `offset = now − Date.now()` corrects a machine whose own
  // clock is minutes out — otherwise that browser would either shut its room early or sit
  // in one the server closed long ago. Re-measured on EVERY poll rather than once: a
  // laptop that slept through the middle of a consultation comes back with a clock that
  // has drifted, and one measurement taken at mount would carry that drift for the rest
  // of the visit.
  const awaitingSince = status?.awaitingSince || null;
  const serverNowIso = status?.now || null;
  const [serverOffsetMs, setServerOffsetMs] = useState(0);
  const [nowMs, setNowMs] = useState(() => Date.now());

  // THE COARSE TICKER: once per `status` OBJECT. `useConsultStatus` sets a fresh object
  // every poll (~4 s), so this is the whole clock most of the time — enough for a phase
  // boundary (which only ever shuts a door a poll later than the server did) and for the
  // solo notice, whose underlying presence snapshot is up to 60 s old anyway.
  useEffect(() => {
    if (serverNowIso) {
      const serverMs = new Date(serverNowIso).getTime();
      // An unparseable instant leaves the previous offset alone rather than resetting it
      // to zero: a bad field must not silently re-enable the uncorrected browser clock.
      if (!Number.isNaN(serverMs)) setServerOffsetMs(serverMs - Date.now());
    }
    setNowMs(Date.now());
  }, [status, serverNowIso]);

  const roomNowMs = nowMs + serverOffsetMs;
  // ONE resolution per render, shared by every block below — two elements painted in one
  // pass cannot disagree about which phase the call is in.
  const phase = roomPhase(status, roomNowMs);
  const roomClosed = phase.phase === "closed";

  // THE FINE TICKER (F-20 originally; now also the two countdowns). A ticking second is
  // the one thing the poll cannot supply — it refreshes every four seconds and the
  // counter would jump — so the instants come from the server and only the CLOCK is
  // local.
  //
  // ITS DEPENDENCY IS A BOOLEAN, deliberately. Depending on `phase.secondsToEnd` would
  // tear down and rebuild the interval once a second, which is not a ticker, it is a
  // treadmill; depending on `phase` (a fresh object each render) would do the same. A
  // boolean re-runs the effect exactly on the transitions that start and stop the tick,
  // and the open room does not re-render once a second underneath its own iframe outside
  // them.
  const countdownActive = phase.countdownActive || awaitingSince != null;
  useEffect(() => {
    if (!countdownActive) return undefined;
    setNowMs(Date.now());
    const id = setInterval(() => setNowMs(Date.now()), 1000);
    return () => clearInterval(id);
  }, [countdownActive]);

  // Fetches the embed door the instant `canJoin` flips true, WITHOUT remounting the
  // component — this is the fix for the "never unlocks without a manual reload" half of
  // BUG #7: `status` keeps polling, and this effect just watches its `canJoin` field.
  //
  // `/access` shares `/join`'s own 403 `AUTH_NOT_READY` gate, and the status poll's own
  // view of `canJoin` can land a beat ahead of it — so a 403 here is retried, exactly
  // like "not yet" rather than a failure. A 404 (contract §2: uniform for a wrong token
  // or a nonexistent consult) is NOT retried — nothing about asking again would change.
  // `consultOver` is a WITHHOLDING condition and not only a rendering one (BUG-055):
  // `/access` is the recorded ARRIVAL as well as the minted URL — it stamps a block's
  // `_stamp_block_arrival` and appends a metered consult's `participant_joined` — so a
  // reload of a finished consultation's page would keep writing presence evidence for a
  // consultation nobody is attending. Nulling `access` here is also what takes the
  // provider frame down, through the one gate the embed already had.
  //
  // `roomClosed` IS IN THE GUARD *AND* IN THE DEPENDENCIES, and it has to be in both:
  // in the guard so the hard close nulls `access` and the iframe unmounts, in the deps so
  // the guard is re-evaluated at the crossing instead of on whatever unrelated change
  // happens to come next. It is not implied by `consultOver`, and neither implies the
  // other: a consultation whose `joinState` still reads `not_yet_open` resolves to the
  // `waiting` phase even once its session is over, so both terms stay.
  useEffect(() => {
    if (!canJoin || consultOver || roomClosed || !appointmentId) {
      setAccess(null);
      setAccessError(null);
      return undefined;
    }
    let cancelled = false;
    let timer = null;

    const attempt = async () => {
      try {
        const result = await gatewayApi.getConsultAccess(appointmentId, isPatient ? token : undefined);
        if (!cancelled) { setAccess(result); setAccessError(null); }
      } catch (e) {
        const code = extractGatewayErrorCode(e);
        const httpStatus = e?.response?.status;
        if (httpStatus === 403 || code === "AUTH_NOT_READY") {
          if (!cancelled) timer = setTimeout(attempt, 2000);
          return;
        }
        if (!cancelled) setAccessError({ code, raw: e });
      }
    };

    attempt();
    return () => { cancelled = true; if (timer) clearTimeout(timer); };
  }, [canJoin, consultOver, roomClosed, appointmentId, token, isPatient, gatewayApi]);

  // REJOINING AFTER A SOLO EJECT. The `room_watch` job removes a participant who has been
  // alone too long, and the room STAYS OPEN behind them — the other party may still
  // arrive, which is why `solo_ejections` is an array and not a write-once stamp. So the
  // way back in is exactly one more `/access`: a fresh meeting token, and the arrival it
  // records is a real one this time.
  //
  // A SEPARATE CALL RATHER THAN A RE-TRIGGER OF THE EFFECT ABOVE, because the two ask
  // different things. That effect is the door opening BY ITSELF and treats a 403 as "not
  // yet, ask again in two seconds"; this is a person pressing a button on a door that was
  // open a minute ago, where a refusal is an answer to show them, not a reason to poll.
  const rejoin = useCallback(async () => {
    if (!appointmentId) return;
    setAccessError(null);
    try {
      const result = await gatewayApi.getConsultAccess(appointmentId, isPatient ? token : undefined);
      setAccess(result);
    } catch (e) {
      setAccessError({ code: extractGatewayErrorCode(e), raw: e });
    }
  }, [appointmentId, token, isPatient, gatewayApi]);

  // THE DEPARTURE BEACON (F-16). Fired ONCE, and only once the room was actually
  // entered: a page that never got past the join window has nobody to record leaving.
  //
  // `pagehide` is the event, not `beforeunload`: `beforeunload` does not fire on mobile
  // Safari at all and is skipped whenever a page enters the back/forward cache, which is
  // precisely the "closed the tab" case this exists for. `visibilitychange -> hidden` is
  // listened to as well because on iOS that is often the LAST event a backgrounded tab
  // ever gets — and the two together are why the ref guard is here rather than optional:
  // a pagehide and a visibilitychange for one departure would be two `participant_left`
  // events, a corrupted timeline rather than a more precise one. (The server dedupes per
  // minute too; this is the cheaper half of the same guarantee.)
  //
  // ALSO ON UNMOUNT, for the SPA case: navigating away from `/consultation/salle` inside
  // the app fires no page-level event at all, and it is a departure just the same.
  //
  // AND DELIBERATELY *NOT* AT THE HARD CLOSE. Crossing `closesAt` unmounts the iframe,
  // not this component, and it must not report a departure: the server ejected the
  // participant on its own clock and already knows the instant, to the second, from its
  // own side. A beacon fired from here would write a SECOND, later `participant_left`
  // from a browser whose clock we have only just finished correcting — a worse timeline,
  // not a more complete one. The unmount case below still fires exactly once.
  const sentLeaveRef = useRef(false);
  // "Did this frame ever actually enter the room?" — a ref and not state, because the
  // listener below must read the CURRENT answer without being torn down and rebuilt on
  // every status poll (a rebuild between a pagehide and its handler is a departure lost).
  // Set in an effect rather than during render: a ref written while rendering is a side
  // effect in the one place React is allowed to call a component twice.
  const enteredRef = useRef(false);
  useEffect(() => { if (access) enteredRef.current = true; }, [access]);
  useEffect(() => {
    if (!appointmentId) return undefined;
    const leave = () => {
      if (sentLeaveRef.current || !enteredRef.current) return;
      sentLeaveRef.current = true;
      // Fire and forget: a page that is closing has nothing to do with a rejection, and
      // the bill does not rest on this call arriving.
      try { gatewayApi.leaveConsult(appointmentId, isPatient ? token : undefined); }
      catch (e) { /* the tab is going away; there is nobody to tell */ }
    };
    const onHidden = () => {
      if (typeof document !== "undefined" && document.visibilityState === "hidden") leave();
    };
    window.addEventListener("pagehide", leave);
    document.addEventListener("visibilitychange", onHidden);
    return () => {
      window.removeEventListener("pagehide", leave);
      document.removeEventListener("visibilitychange", onHidden);
      leave();
    };
  }, [appointmentId, token, isPatient, gatewayApi]);

  // FULLSCREEN — THE TARGET IS THE CARD, NOT THE `<iframe>`. Everything this page says
  // about a call in flight — the cap banner, the ending-soon and overrun countdowns, the
  // solo warning — lives OUTSIDE the embed, so fullscreening the embed alone would hide,
  // for the whole duration of the consultation, precisely the notices the room-lifecycle
  // work exists to deliver. Promoting the card carries them onto the big screen with it.
  //
  // NOTHING IS REMOUNTED AND NOTHING IS RE-KEYED — the same rule the sibling slots above
  // are written to obey. Fullscreen promotes the EXISTING DOM node into the top layer;
  // the iframe keeps its `src`, its index among its siblings and its media session, and
  // the toggle below swaps class names on that one tree rather than rendering a second,
  // fullscreen copy of it. A copy would mean a second `<iframe src=access.url>`, i.e. a
  // second participant in the room on the same token.
  const frameRef = useRef(null);
  const [isFullscreen, setIsFullscreen] = useState(false);

  // CAPABILITY, NOT BROWSER-SNIFFING, and not an assumption either: a browser with no
  // element fullscreen (iOS Safari has never had one) gets no button at all rather than
  // a button that does nothing when pressed.
  const canFullscreen = typeof document !== "undefined"
    && Boolean(document.fullscreenEnabled || document.webkitFullscreenEnabled);

  // THE BROWSER IS THE SOURCE OF TRUTH FOR THE FLAG, NEVER THE CLICK. Escape, F11 and the
  // UA's own exit chrome all leave fullscreen without passing through our handler, and a
  // flag set optimistically inside `toggleFullscreen` would strand the card in its
  // fullscreen classes — `w-full h-full`, no max width — inside an ordinary page.
  useEffect(() => {
    const sync = () => {
      const current = document.fullscreenElement || document.webkitFullscreenElement;
      // BOTH HALVES. `frameRef.current` is null on the loading and missing-context
      // branches, and so is `current` — a bare identity test would call that fullscreen.
      setIsFullscreen(Boolean(current) && current === frameRef.current);
    };
    document.addEventListener("fullscreenchange", sync);
    document.addEventListener("webkitfullscreenchange", sync);
    sync();
    return () => {
      document.removeEventListener("fullscreenchange", sync);
      document.removeEventListener("webkitfullscreenchange", sync);
    };
  }, []);

  const toggleFullscreen = useCallback(() => {
    const el = frameRef.current;
    if (!el) return;
    const mine = (document.fullscreenElement || document.webkitFullscreenElement) === el;
    const fn = mine
      ? (document.exitFullscreen || document.webkitExitFullscreen)
      : (el.requestFullscreen || el.webkitRequestFullscreen);
    if (typeof fn !== "function") return;
    // `requestFullscreen` REJECTS, it does not throw, when the gesture is untrusted or a
    // permissions policy refuses. Swallowing the rejection leaves the page exactly as it
    // was — the room is still embedded and the new-tab escape hatch is still on screen —
    // which is the honest outcome; an unhandled rejection would be noise, not news.
    Promise.resolve(fn.call(mine ? document : el)).catch(() => {});
  }, []);

  // THE BUTTON LIVES WITH THE EMBED, so the instant the room comes down there is no
  // longer an on-screen way back out of fullscreen — only Escape, which is not
  // discoverable and which a patient reading a "your consultation has ended" panel has no
  // reason to try. Leaving on the same fact that unmounts the iframe keeps the two in
  // step. Scoped to OUR element: another fullscreen surface on the page is not ours to
  // close.
  const roomEmbedded = canJoin && Boolean(access) && !roomClosed;
  useEffect(() => {
    if (roomEmbedded) return;
    const current = document.fullscreenElement || document.webkitFullscreenElement;
    if (!current || current !== frameRef.current) return;
    const exit = document.exitFullscreen || document.webkitExitFullscreen;
    if (typeof exit === "function") Promise.resolve(exit.call(document)).catch(() => {});
  }, [roomEmbedded]);

  if (!appointmentId || (isPatient && !token)) {
    return (
      <div className="text-center text-sm text-eh-muted py-8" data-testid="consult-room-missing-context">
        {t("consult.room.missingContext")}
      </div>
    );
  }

  if (statusLoading && !status) {
    return (
      <div className="flex items-center justify-center gap-2 text-eh-muted py-12" data-testid="consult-room-loading">
        <Loader2 className="w-5 h-5 animate-spin" /> {t("consult.room.loading")}
      </div>
    );
  }

  if (statusError && !status) {
    return (
      <div className="text-center py-8" data-testid="consult-room-error">
        <AlertTriangle className="w-6 h-6 text-rose-500 mx-auto mb-2" />
        <p className="text-sm text-rose-600" role="alert">{t("consult.room.loadError")}</p>
      </div>
    );
  }

  const statusKey = status ? SESSION_STATUS_KEYS[status.sessionStatus] : null;
  // A FIXED BLOCK never has a session, so `statusKey` is permanently null for one and the
  // fallback below would tell a client who paid weeks ago that we are waiting for their
  // authorisation. Its own status is the appointment's: delivered, or ready when the
  // window opens. There is nothing in between to describe, because there is no meter.
  const blockStatusKey = (status && isFixedBlock(status))
    ? (status.bookingStatus === "completed"
        ? "consult.room.status.blockCompleted"
        : blockNotPaid
          ? "consult.room.joinState.blockNotPaid"
          : (canJoin ? "consult.room.status.readyToJoin" : null))
    : null;
  const titleKey = isPatient ? "consult.room.title" : "consult.room.practitionerTitle";
  // `authState: "failed"` (contract §2, AMENDED 2026-08-27): the scheduled lane's T-48h
  // off-session authorisation was declined or the card expired. `/join`/`/access` keep
  // answering 403 AUTH_NOT_READY — the door will not open until a NEW authorisation
  // lands — so this is rendered instead of the generic "waiting" copy, not beside it.
  const authFailed = status?.authState === "failed";
  // `capReachedAt` (contract §2, AMENDED 2026-08-27): the write-once instant billing hit
  // the authorised ceiling. `null` means "not reached", never "unknown" — so the banner
  // keys strictly off non-null, and stays up for BOTH roles for as long as the frame is
  // mounted (the call itself is not stopped, only the meter — contract's own wording).
  const capReached = status?.capReachedAt != null;
  // `awaitingSince` is non-null only while the server itself calls this an outstanding
  // authorisation, so this branch cannot claim a wait that is over. `authState` is read
  // beside it and not instead of it: `none` (no row, or a released hold) is a different
  // situation with nothing to count.
  //
  // MEASURED ON THE SERVER-CORRECTED CLOCK, like every other instant on this screen:
  // `awaitingSince` is the server's, and subtracting it from an uncorrected browser clock
  // is how a machine five minutes fast reports a five-minute wait one second in.
  const waitedSeconds = (awaitingSince && status?.authState === "awaiting")
    ? Math.max(0, Math.round((roomNowMs - new Date(awaitingSince).getTime()) / 1000))
    : null;
  const reconcileAfter = status?.authReconcileAfterSeconds;
  // Past the threshold the server is actively asking Stripe, so the screen says so. It
  // is the SERVER's number: the threshold is operator-editable, and a constant mirrored
  // here would let the screen contradict the server about when a wait became unusual.
  const confirmingWithProvider = (
    waitedSeconds != null && typeof reconcileAfter === "number"
    && waitedSeconds >= reconcileAfter
  );

  // ── THE END OF THE BOOKED TIME ───────────────────────────────────────────────
  //
  // EACH BLOCK IS GATED ON THE VALUES IT INTERPOLATES, not on the phase alone. Every
  // instant on the wire is nullable, a null REMOVES a boundary rather than licensing a
  // locally computed one, and a sentence with a hole where its time should be is worse
  // than no sentence.
  //
  // OUR COPY IS ABOUT THE BOOKING, NEVER ABOUT THE ROOM. Measured 2026-09-10: because
  // the Daily room's own `exp` is the hard close, the Prebuilt UI renders its own "The
  // meeting will end in M:SS" INSIDE the iframe, counting to a different instant than
  // ours. Two countdowns on one screen are only tolerable while they are visibly about
  // two different things — ours says the client's booked time is ending, Daily's says the
  // room is closing — so nothing here may be reworded into a statement about the room.
  const endsAtClock = phase.endsAt ? fmtClock(phase.endsAt, lang) : "";
  const showEndingSoon = phase.phase === "endingSoon"
    && phase.secondsToEnd != null && !!endsAtClock;
  const showOverrun = phase.phase === "overrun"
    && phase.secondsToClose != null && !!endsAtClock;
  // WHOLE MINUTES, NEVER SECONDS. The solo run is decided by `room_watch` from a presence
  // snapshot up to 60 s old, on a 60 s poll — a ticking second over a minute-resolution
  // fact claims a precision the server never offered. `Math.max(1, …)` for the same
  // reason and not for cosmetics: below one minute this statement has no resolution left,
  // and "0 min" would name an instant that has demonstrably already passed while the
  // reader is still connected, which is the one thing it must not say.
  const soloMinutesLeft = (phase.solo.active && phase.solo.secondsLeft != null)
    ? Math.max(1, Math.ceil(phase.solo.secondsLeft / 60))
    : null;
  const showSolo = !roomClosed && soloMinutesLeft != null;
  const showEjected = !roomClosed && phase.ejected.active;

  return (
    <div
      ref={frameRef}
      className={`bg-white border border-eh-border-soft p-6 space-y-4 ${
        isFullscreen
          ? "rounded-none max-w-none w-full h-full overflow-y-auto flex flex-col"
          : "rounded-2xl max-w-2xl mx-auto"
      }`}
      data-testid="consult-room-frame"
      data-fullscreen={isFullscreen ? "true" : "false"}
    >
      <h1 className="font-display text-2xl text-eh-ink">{t(titleKey)}</h1>

      {/* Persistent, both roles, for as long as capReachedAt is non-null — the defect
          this fixes is silent: the server stamped the fact, `/status` returned it, and
          NOTHING rendered it, so billing stopped while the call carried on and the first
          either party heard of it was the invoice. */}
      {capReached && (
        <div
          role="status"
          className="rounded-xl border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-800 flex items-start gap-2"
          data-testid="consult-room-cap-reached-banner"
        >
          <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />
          <span>{t("consult.room.capReached")}</span>
        </div>
      )}

      {/* SIBLING SLOTS, EACH ITS OWN `{cond && …}`, in the cap-reached banner's own
          hand-rolled amber idiom rather than a shadcn `Alert` — one visual vocabulary for
          "something about this call has changed", and no new component boundary between
          a sentence and the state that produced it.
          THEY ARE NOT AN ARRAY AND THEY ARE NOT KEYED. Nothing on this page may become a
          list or acquire a `key` that varies with the phase: React reconciles the iframe
          below by position, and the first re-key would remount it and tear down the media
          session — camera, microphone and all — in the middle of a consultation. */}
      {showEndingSoon && (
        <div
          role="status"
          className="rounded-xl border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-800 flex items-start gap-2"
          data-testid="consult-room-ending-soon-banner"
        >
          <Clock className="w-4 h-4 flex-shrink-0 mt-0.5" />
          <span>
            {t("consult.room.endingSoon",
               { when: endsAtClock, remaining: fmtDuration(t, phase.secondsToEnd) })}
          </span>
        </div>
      )}

      {showOverrun && (
        <div
          role="status"
          className="rounded-xl border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-800 flex items-start gap-2"
          data-testid="consult-room-overrun-banner"
        >
          <Clock className="w-4 h-4 flex-shrink-0 mt-0.5" />
          <span>
            {t("consult.room.overrun.notice",
               { when: endsAtClock, remaining: fmtDuration(t, phase.secondsToClose) })}
          </span>
        </div>
      )}

      {/* ORTHOGONAL TO THE PHASE, and rendered beside it rather than instead of it: being
          alone in the room and the booked time running out are two independent facts, and
          both can be true at once. The sentence differs by ROLE because the situation
          does — a client alone is waiting for their practitioner, a practitioner alone is
          waiting for a client who may not come. */}
      {showSolo && (
        <div
          role="status"
          className="rounded-xl border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-800 flex items-start gap-2"
          data-testid="consult-room-solo-banner"
        >
          <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />
          <span>
            {t(isPatient ? "consult.room.solo.patient" : "consult.room.solo.practitioner",
               { remaining: t("consult.room.duration.minutes", { count: soloMinutesLeft }) })}
          </span>
        </div>
      )}

      {/* THE ROOM IS STILL OPEN AFTER A SOLO EJECT — that is the whole point of the rule
          (the other party may still arrive), which is why this offers a way back in
          rather than a way out. */}
      {showEjected && (
        <div
          role="status"
          className="rounded-xl border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-800 space-y-2"
          data-testid="consult-room-ejected-panel"
        >
          <p className="flex items-start gap-2">
            <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />
            <span>{t("consult.room.ejected.notice")}</span>
          </p>
          <button
            type="button"
            onClick={rejoin}
            data-testid="consult-room-rejoin"
            className="btn-ghost text-sm inline-flex"
          >
            {t("consult.room.ejected.rejoin")}
          </button>
        </div>
      )}

      {/* THE HARD CLOSE. The iframe is already gone by the time this renders — the access
          effect nulls `access` on the same `roomClosed` — so this is what is left on the
          screen, and it must say why.
          `!consultOver` IS LOAD-BEARING AND NOT TIDINESS. Both facts close the phase, but
          only one of them is about time: a practitioner who ends a 30-minute consultation
          at minute 10 leaves a session that is over and a booked time that plainly is
          not, and this panel would have told both parties their booked time had run out
          twenty minutes before it did. That case is the BUG-055 block further down, which
          states the SESSION's outcome; this one states that the CLOCK shut the door. */}
      {roomClosed && !consultOver && (
        <div
          role="status"
          className="rounded-xl border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-800 space-y-1"
          data-testid="consult-room-closed-panel"
        >
          <p className="font-medium">{t("consult.room.closed.panel")}</p>
          <p className="text-amber-700">{t("consult.room.closed.hint")}</p>
        </div>
      )}

      {/* GATED ON THE MODEL AS WELL AS ON THE VALUE. Withholding the FETCH is not enough:
          `useConsultQuote` keeps its last result when its appointment id is withdrawn, so
          a quote resolved during the window before `pricingMode` arrived would stay on
          screen. A render must not depend on a hook having cleared itself. */}
      {quote && !isFixedBlock(status) && (
        <p className="text-sm text-eh-muted" data-testid="consult-room-ceiling">
          {t("consult.room.ceilingReminder", { amount: fmtMoney(quote.ceilingCents, quote.currency) })}
        </p>
      )}

      <div className="rounded-xl bg-eh-cream px-4 py-4 text-center space-y-3" data-testid="consult-room-status">
        {authFailed ? (
          <p className="text-sm text-eh-ink" data-testid="consult-room-payment-action-required">
            {t(isPatient ? "consult.reauth.explain" : "consult.room.authFailed.practitioner")}
          </p>
        ) : waitedSeconds != null ? (
          /* WAITING FOR THE MONEY, and the money question is answered BEFORE the time
             question — the same order `consults.join()` runs its two gates in, and for
             the same reason: "you are not authorised yet" and "it is not time yet" are
             both true of an unauthorised booking outside its window, and only the first
             is something the reader can act on. Rendering the join-window line here
             would also state "Payment authorised" over a payment that is not. */
          <div className="space-y-2" data-testid="consult-room-awaiting-auth">
            <p className="text-sm text-eh-ink flex items-center justify-center gap-2">
              <Clock className="w-4 h-4 flex-shrink-0" />
              <span>
                {t("consult.room.waitingAuth.waiting",
                   { elapsed: fmtDuration(t, waitedSeconds) })}
              </span>
            </p>
            {confirmingWithProvider && (
              <p className="text-xs text-eh-muted" data-testid="consult-room-confirming">
                {t("consult.room.waitingAuth.confirming")}
              </p>
            )}
          </div>
        ) : roomNotOpen ? (
          /* AUTHORISED, BUT NOT YET. The money is held and the appointment is real — the
             one thing this page must not do is look like a live consultation, which is
             what mounting the room a day early did. `opensAt` is the server's instant,
             formatted in the reader's own locale. */
          <p className="text-sm text-eh-ink flex items-center justify-center gap-2"
             data-testid="consult-room-not-open">
            <Clock className="w-4 h-4 flex-shrink-0" />
            <span>
              {blockNotPaid
                ? t("consult.room.joinState.blockNotPaid")
                : joinState === "not_yet_open" && status?.opensAt
                  ? t(isFixedBlock(status)
                        ? "consult.room.joinState.blockOpensAt"
                        : "consult.room.joinState.opensAt",
                      { when: fmtWhen(status.opensAt, lang) })
                  : t(JOIN_STATE_KEYS[joinState] || "consult.room.status.waitingAuth")}
            </span>
          </p>
        ) : (
          <p className="text-sm text-eh-ink">
            {statusKey
              ? t(statusKey)
              : blockStatusKey
                ? t(blockStatusKey)
                : t(canJoin ? "consult.room.status.readyToJoin" : "consult.room.status.waitingAuth")}
          </p>
        )}

        {authFailed && isPatient && (
          <ConsultReauthPanel
            appointmentId={appointmentId}
            token={token}
            cancelPath="/consultation/salle"
            quote={quote}
            gatewayApi={gatewayApi}
          />
        )}
      </div>

      {/* `!roomClosed` HERE AS WELL AS IN THE ACCESS EFFECT, and it is not belt-and-braces:
          `rejoin` above resolves asynchronously, so a press a fraction of a second before
          the hard close can land its `setAccess` AFTER the effect has already nulled it,
          and remount a live room on a consultation the server has ended. The effect is
          what stops the door being re-opened; this is what stops a door already in flight
          from being rendered. */}
      {canJoin && access && !roomClosed && (
        <div
          className={`space-y-2 ${isFullscreen ? "flex-1 flex flex-col min-h-0" : ""}`}
          data-testid="consult-room-embed"
        >
          {/* `allowFullScreen` IS FOR THE PROVIDER'S OWN CONTROL, not for ours: our button
              below fullscreens the card from this document and needs no permission from
              the embed. Without the attribute Daily's in-room expand button is inert, and
              a user who reached for the control they already know would find it dead. */}
          <iframe
            title={t(titleKey)}
            src={access.url}
            allow="camera; microphone; display-capture; fullscreen"
            allowFullScreen
            className={`w-full rounded-xl border border-eh-border-soft ${
              isFullscreen ? "flex-1 min-h-0" : "aspect-video"
            }`}
            data-testid="consult-room-iframe"
          />
          {/* Some providers refuse to be framed (X-Frame-Options / CSP frame-ancestors),
              and a browser gives JS no reliable signal when that happens — the iframe
              just renders blank, with no error event to catch. The honest fix is not
              trying to detect the refusal; it is always offering this escape hatch,
              same-tab-open-a-new-tab, so THIS page (and its heartbeat) keeps running
              underneath regardless of whether the embed itself worked. */}
          <div className="flex flex-wrap items-center justify-center gap-x-4 gap-y-2">
            {canFullscreen && (
              <button
                type="button"
                onClick={toggleFullscreen}
                aria-pressed={isFullscreen}
                data-testid="consult-room-fullscreen"
                className="btn-ghost text-sm inline-flex items-center gap-1.5"
              >
                {isFullscreen
                  ? <Minimize2 className="w-4 h-4" aria-hidden="true" />
                  : <Maximize2 className="w-4 h-4" aria-hidden="true" />}
                <span>
                  {t(isFullscreen ? "consult.room.fullscreen.exit" : "consult.room.fullscreen.enter")}
                </span>
              </button>
            )}
            <p className="text-xs text-eh-muted text-center">
              {t("consult.room.framingFallbackHint")}{" "}
              <a
                href={access.url}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="consult-room-new-tab"
                className="underline"
              >
                {t("consult.room.openInNewTab")}
              </a>
            </p>
          </div>
        </div>
      )}

      {canJoin && accessError && (
        <p className="text-sm text-rose-600 text-center" role="alert" data-testid="consult-room-access-error">
          {t("consult.room.accessError")}
        </p>
      )}

      {/* BUG-055 — THE CLOSE-OUT. The room is a third-party surface: the provider's own
          red hang-up button navigates ITS iframe to ITS own close page, and a plain
          cross-origin `<iframe>` gives this page no channel to hear about it (the only
          supported way to hear a Jitsi call end is `JitsiMeetExternalAPI`'s
          `readyToClose`, which is a different embed entirely). What this page CAN do, and
          did not, is state the outcome on the server's own authority the moment the
          consultation is over, and always leave a door back into the practice — a client
          whose call has ended had no route back to the practice site from here at all,
          while the practitioner's own room page has carried one all along
          (`admin/telehealth/ConsultRoomPage.jsx`, `consult-room-back`). */}
      {consultOver && (
        <div className="text-center space-y-3" data-testid="consult-room-ended">
          <p className="text-sm text-eh-ink">{t("consult.room.ended.confirmation")}</p>
          {endedSlot}
        </div>
      )}

      {/* RENDERED WHENEVER THERE IS ONE TO RENDER, not only once the call is over: a
          patient who hung up inside the provider frame is looking at the provider's page
          with our chrome around it, and that is the moment they need this most. */}
      {homePath && (
        <p className="text-center">
          <a
            href={homePath}
            data-testid="consult-room-home"
            className="btn-ghost text-sm inline-flex"
          >
            {t("consult.room.ended.backToPractice")}
          </a>
        </p>
      )}

      {/* PRESENCE IS EVIDENCE OF A BILL, AND A FIXED BLOCK HAS NO BILL TO EVIDENCE. The
          sentence is literally true of Flow C — the heartbeat this page runs is what the
          minutes are computed from — and false of a block paid in full at booking, where
          leaving the tab changes nothing about what anyone is charged. Told to a patient
          it invents an obligation; told to a practitioner it invents a risk. */}
      {/* `!roomClosed` for the same reason as `!consultOver`, one clock later: past the
          hard close there is no room to stay on the page FOR, and telling someone their
          presence is still being recorded when the door has shut invents an obligation
          out of a call that is over. */}
      {!isFixedBlock(status) && !consultOver && !roomClosed && (
        <p className="text-xs text-eh-muted text-center" data-testid="consult-room-stay-hint">
          {t("consult.room.stayOnPageHint")}
        </p>
      )}
    </div>
  );
}
